import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MetricCard } from '@/components/MetricCard';
import { PieChart, BarChart, GaugeChart, ChartContainer } from '@/components/charts';
import { useExecutiveSummary } from '../hooks';
import { RefreshCw, Thermometer, Building2, Radio, AlertTriangle, Calendar, TrendingUp, Download, Flame } from 'lucide-react';

export default function ExecutiveOverviewPage() {
  const { t } = useTranslation('intelligence');
  const { data, isLoading, error, refetch, isFetching } = useExecutiveSummary();

  const alertsChartData = data ? [
    { name: t('executive.charts.open'), value: data.alerts?.open_alerts || 0 },
    { name: t('executive.charts.acknowledged'), value: data.alerts?.acknowledged_alerts || 0 },
    { name: t('executive.charts.closedToday'), value: data.alerts?.closed_alerts || 0 },
  ] : [];

  const devicesChartData = data ? [
    { name: t('executive.charts.online'), value: (data.devices?.total_devices || 0) - (data.devices?.offline_count || 0) },
    { name: t('executive.charts.offline'), value: data.devices?.offline_count || 0 },
    { name: t('executive.charts.lowBattery'), value: data.devices?.low_battery_count || 0 },
    { name: t('executive.charts.weakSignal'), value: data.devices?.weak_signal_count || 0 },
  ] : [];

  const formatMinutes = (minutes: number | undefined) => {
    if (!minutes) return '--';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const onlineRate = data?.devices?.total_devices && data.devices.total_devices > 0
    ? (((data.devices.total_devices - (data.devices.offline_count || 0)) / data.devices.total_devices) * 100)
    : 0;

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-red-700">{t('common:error')}: {error.message}</span>
          <Button variant="outline" onClick={() => refetch()}>
            {t('common:retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('executive.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('executive.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          {t('common:refresh')}
        </Button>
      </div>

      {/* Platform Summary - 4 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title={t('executive.cards.activeSensors')}
          value={data?.platform?.active_sensors}
          icon={<Thermometer className="h-5 w-5" />}
          color="green"
          loading={isLoading}
        />
        <MetricCard
          title={t('executive.cards.totalOrganisations')}
          value={data?.platform?.total_organisations}
          icon={<Building2 className="h-5 w-5" />}
          color="blue"
          loading={isLoading}
        />
        <MetricCard
          title={t('executive.cards.totalTransmitters')}
          value={data?.platform?.total_transmitters}
          icon={<Radio className="h-5 w-5" />}
          color="purple"
          loading={isLoading}
        />
        <MetricCard
          title={t('executive.cards.openAlerts')}
          value={data?.alerts?.open_alerts}
          icon={<AlertTriangle className="h-5 w-5" />}
          color="red"
          loading={isLoading}
        />
      </div>

      {/* Charts Row - Alerts Overview + Device Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartContainer title={t('executive.sections.alertsOverview')} loading={isLoading} height={280}>
          <div className="flex flex-col h-full">
            <div className="flex-1">
              <PieChart
                data={alertsChartData}
                height={180}
                colors={['#e74c3c', '#f39c12', '#27ae60']}
                showLegend
                innerRadius={60}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">{t('executive.metrics.mtta')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatMinutes(data?.alerts?.avg_mtta_minutes)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">{t('executive.metrics.mttr')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatMinutes(data?.alerts?.avg_mttr_minutes)}
                </p>
              </div>
            </div>
          </div>
        </ChartContainer>

        <ChartContainer title={t('executive.sections.deviceHealth')} loading={isLoading} height={280}>
          <div className="flex flex-col h-full">
            <div className="flex-1">
              <BarChart
                data={devicesChartData}
                height={180}
                colors={['#27ae60', '#e74c3c', '#f39c12', '#9b59b6']}
                colorByValue
              />
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">{t('executive.metrics.onlineRate')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {onlineRate.toFixed(1)}%
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">{t('executive.metrics.totalDevices')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {data?.devices?.total_devices?.toLocaleString() || '--'}
                </p>
              </div>
            </div>
          </div>
        </ChartContainer>
      </div>

      {/* Operations Section - SLA Gauge */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('executive.sections.operations')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-1">
              {isLoading ? (
                <Skeleton className="h-[200px] w-full" />
              ) : (
                <GaugeChart
                  value={data?.operations?.sla_met_rate || 0}
                  max={100}
                  height={200}
                  label={t('executive.metrics.slaCompliance')}
                  suffix="%"
                />
              )}
            </div>
            <div className="md:col-span-2 grid grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">{t('executive.metrics.openTickets')}</p>
                {isLoading ? (
                  <Skeleton className="h-8 w-16 mx-auto mt-2" />
                ) : (
                  <p className="text-3xl font-bold text-gray-900">
                    {data?.operations?.open_tickets?.toLocaleString() || 0}
                  </p>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">{t('executive.metrics.overdueTickets')}</p>
                {isLoading ? (
                  <Skeleton className="h-8 w-16 mx-auto mt-2" />
                ) : (
                  <p className={`text-3xl font-bold ${(data?.operations?.overdue_tickets || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {data?.operations?.overdue_tickets?.toLocaleString() || 0}
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics - 4 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title={t('executive.cards.alertsToday')}
          value={data?.alerts?.alerts_today}
          icon={<Calendar className="h-5 w-5" />}
          color="orange"
          loading={isLoading}
        />
        <MetricCard
          title={t('executive.cards.escalatedAlerts')}
          value={data?.alerts?.escalated_count}
          icon={<TrendingUp className="h-5 w-5" />}
          color="red"
          loading={isLoading}
        />
        <MetricCard
          title={t('executive.cards.pendingFirmware')}
          value={data?.devices?.firmware_pending_count}
          icon={<Download className="h-5 w-5" />}
          color="blue"
          loading={isLoading}
        />
        <MetricCard
          title={t('executive.cards.highPriorityTickets')}
          value={data?.operations?.high_priority_open}
          icon={<Flame className="h-5 w-5" />}
          color="orange"
          loading={isLoading}
        />
      </div>
    </div>
  );
}
